import { ISpotCard } from '@exp/common/src/interfaces/generic';
export const cpScoreBoostContent = {
  headline: 'Instantly raise your credit scores for free',
  subheadline: `Get credit for bills like your cell phone, utilities, rent and insurance with Experian Boost<sup>®</sup>.<sup>ø</sup>`,
  heroCTA: 'Start your boost',
  herounderCTAcopy: 'No credit card required',
  heroCards: [
    {
      icon: 'ecs-font-icon-frs-rocket-boost',
      heading: 'Using Experian Boost could improve your credit scores—fast.',
    },
    {
      icon: 'ecs-font-icon-frs-report-experian',
      heading: 'Building your credit history helps show lenders you’re reliable.',
    },
    {
      icon: 'ecs-font-icon-ccm-credit-cards',
      heading: 'Expanding your Experian credit file could mean more card & loan offers.',
    },
  ],
  stickyHeading: 'Boost your FICO<sup>®</sup> Score* instantly for free',
  stickyBtnCopy: 'Start your boost',
  secondary: {
    title: 'How it works',
    btn: 'Start your boost',
    bullets: [
      {
        heading: 'Connect your bank accounts',
        subcopy:
          'Add any bank accounts you use to pay your bills. Your information remains private.',
        image: '/graphics/graphic-boost-select-bank-account-screen',
        alt: 'Graphic boost select bank account screen.',
      },
      {
        heading: 'Select the bills you want to use',
        subcopy: `We’ll detect bills with on-time payments, and you can add them to your Experian credit file.`,
        image: '/graphics/graphic-boost-add-bills-screen',
        alt: 'Graphic boost add bills screen.',
      },
      {
        heading: 'See your results instantly',
        subcopy: `You'll find out right away if your FICO<sup>®</sup> Score* increased and by how many points.`,
        image: '/graphics/graphic-simulated-score-boost-feature',
        alt: 'Graphic simulated score boost feature.',
      },
    ],
  },
  spoticonsSection: {
    title: 'Included with Experian Boost',
    steps: [
      {
        image: { src: '/icons/icon-score.svg' },
        heading: 'Access your FICO<sup>®</sup> Score and credit report',
        subcopy:
          'Stay up to date with your latest Experian credit report and FICO<sup>®</sup> Score every 30 days.',
      },
      {
        image: { src: '/icons/icon-credit-cards-front-and-back.svg' },
        heading: 'Get credit card and loan offers matched to you',
        subcopy:
          'See personalized offers based on your unique credit profile and find the best one for you.',
      },
      {
        image: { src: '/icons/icon-web-security.svg' },
        heading: 'Run a free identity scan',
        subcopy: 'Check if your personal info is exposed on the dark web and people finder sites.',
      },
      {
        image: { src: '/icons/icon-car.svg' },
        heading: 'Save on car insurance',
        subcopy: 'Shop and compare quotes from top insurers to find your same coverage for less.',
      },
    ] as ISpotCard[],
    btn: 'Start your boost',
  },
  mapTitle: 'Millions of points boosted nationwide',
  faq: {
    title: 'Education & advice',
    content: [
      {
        heading: 'What is Experian Boost?',
        subcopy: `<p>Experian Boost is an easy way for you to take control of your credit and build long-term credit health—just by paying your bills. When you connect your bank or credit card, we’ll look for bills with positive history that you can add to your Experian credit file. It could also instantly raise your FICO<sup>®</sup> Score!</p>`,
      },
      {
        heading: 'How does Experian Boost work?',
        subcopy: `<p>Good question! Connect to the bank or credit card account you use to pay your bills and/or rent. We’ll look through 2 years of payment history for any qualifying bills that have at least 3 payments in the last 6 months (including 1 payment within the last 3 months).</p><p>Once we find them, all you need to do is verify that the info is correct, and we’ll add them to your Experian credit file.</p>`,
      },
      {
        heading: 'How is Experian Boost different?',
        subcopy: `<p>Unlike credit repair companies, Experian Boost is completely free and can increase your credit scores fast. Credit repair may cost you thousands of dollars and only help fix inaccuracies, which you can do yourself for free. Piggybacking services that add you to a stranger's account are risky and considered deceptive by lenders. Raise your credit scores securely with Experian Boost.</p>`,
      },
      {
        heading: 'Is Experian Boost secure?',
        subcopy: `<p>Absolutely. We use bank-level SSL security encryption to make sure your data is safe when you connect your accounts and add your bills. It’s our top priority.</p>`,
      },
      {
        heading: 'Which bill types qualify?',
        subcopy: `<p>These are the bills that currently qualify for Experian Boost:</p><ul><li>Mobile and landline phone</li><li>Rent payments</li><li>Utility, including electricity, gas, water and waste management</li><li>Telecom, including satellite, cable and television</li><li>Insurance</li><li>Internet</li><li>Video streaming services</li></ul>`,
      },
      {
        heading: 'What types of insurance qualify?',
        subcopy: `<p>Experian Boost accepts most insurance payments, including home, auto, life and more. Health insurance payments or any insurance payments that aren't paid monthly don't qualify.</p><p>If your insurance meets these requirements and we haven't found it, make sure the account used to make payments is connected.</p>`,
      },
      {
        heading: 'Which types of rent payments qualify?',
        subcopy: `<p>Only online residential rent payments made to select property management companies or rent payment platforms are eligible. Rent payments made with cash, money order, personal check or on a mobile payment transfer app (like PayPal, Venmo or Zelle®) aren't eligible.</p><p>If you have an active mortgage account or any other rent tradeline on your Experian credit file, then your rent isn't eligible either. <a href="/help/boost-disclosure.html">Learn more</a>.</p>`,
      },
      {
        heading: 'If I have a mortgage, is my rent payment eligible?',
        subcopy: `<p>Your rent isn't eligible if you have an active mortgage tradeline on your Experian credit file.</p>`,
      },
      {
        heading: 'Can I use my video streaming service bills?',
        subcopy: `<p>You sure can! Video streaming services like Netflix®, Disney+™, HBO™ and Hulu™ are all eligible. Just make sure the accounts are in your name and the bills have 3 payments in the last 6 months (including 1 payment within the last 3 months).</p><p>Want to know when we add new video streaming services to our list of eligible providers? Make sure you opt in for notifications in your <a href="https://usa.experian.com/member/ngx-profile/communications">Experian account</a>.</p>`,
      },
      {
        heading: 'What if I’m having trouble connecting to my bank or credit card?',
        subcopy: `<p>If you're having trouble connecting your bank to Experian Boost®, your bank login credentials might be incorrect. We recommend logging in to your bank' s website to make sure your login info is up to date. Then, go to your bank settings to confirm your contact info matches your Experian membership profile.</p><p>If you're seeing a message that Experian Boost can' t connect to your bank, your bank might not be supported yet, or it might be a technical error. We recommend trying again and following the prompts on the screen if it still says you can't connect to your bank.</p>`,
      },
      {
        heading: 'What if my bank or credit card isn’t listed?',
        subcopy: `<p>If your bank or credit card isn’t listed, or you search for it and it doesn’t appear in the results, select the "Can't find your bank?" link and follow the instructions.</p>`,
      },
      {
        heading:
          'What if the name on the account I’m connecting to Experian Boost doesn’t match my Experian account?',
        subcopy: `<p>For your security, we need to verify that the name on your connected account matches the name on your Experian account. If the name on your connected account is in the name of a business, trust or other financial conservator, you'll need to update this information before you use Experian Boost.</p>`,
      },
      {
        heading:
          'Can Experian Boost help me if I have no credit history or no FICO<sup>®</sup> Score?',
        subcopy: `<p>If you're just starting out on your credit journey, that’s OK. Experian Boost can still help you if you meet the minimum FICO<sup>®</sup> requirements.</p><ul><li>You have at least 1 account on your credit report that has been active for at least 6 months.</li><li>You have at least 1 account on your credit report that has been reported to a credit bureau within the last 6 months.</li><li>You are not listed as “deceased” on your credit report. (And if that's the case, the credit bureau providing your report might have you mixed up with another person.)</li><li>If you check all these boxes, you can get your first FICO® Score and use Experian Boost.</li></ul>`,
      },
    ],
  },
};
